import Utils from "../utils/Utils";

const sanitizeUserUpdateLog = (payload = {}) => {
  if (!payload?.createCustomBucketObjectDTO) {
    return payload;
  }

  return {
    ...payload,
    createCustomBucketObjectDTO: {
      ...payload.createCustomBucketObjectDTO,
      data: "[base64 omitted]",
    },
  };
};

const userController = {
  // =========================================================
  // AUTHENTICATE
  // =========================================================
  authenticate: ({
    authenticateRequest,
    authenticationPortalType = "ADMIN_PORTAL",
  }) => {
    return Utils.controller(
      process.env.REACT_APP_AUTHENTICAT_USER_API || "api/user/v1/authenticate",
      authenticateRequest,
      "POST",
      `authenticationPortalType=${authenticationPortalType}`,
      false,
      "",
      true,
    );
  },

  // =========================================================
  // UPDATE USER
  // =========================================================
  updateUser: ({ userUpdateRequest, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const safePayloadLog = sanitizeUserUpdateLog(userUpdateRequest);

    return Utils.controller(
      "api/user/v1/updateUser",
      userUpdateRequest,
      "PUT",
      "",
      true,
      token,
      true,
    );
  },

  // =========================================================
  // GET USER
  // =========================================================
  getUser: ({ userID, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const normalizedUserID = String(userID || "").trim();

    const query = `userID=${encodeURIComponent(normalizedUserID)}`;

    return Utils.controller(
      "api/user/v1/user",
      "",
      "GET",
      query,
      true,
      token,
      true,
    );
  },

  // =========================================================
  // RE AUTHENTICATE
  // =========================================================
  reAuthenticate: ({ jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    return Utils.controller(
      "api/user/v1/reAuthenticate",
      "",
      "POST",
      "",
      true,
      token,
      true,
    );
  },

  // =========================================================
  // REGISTER
  // =========================================================
  register: ({ userRegisterRequest, isAdmin = false }) => {
    return Utils.controller(
      "api/user/v1/authenticate/register",
      userRegisterRequest,
      "POST",
      `isAdmin=${false}`,
      false,
      "",
      true,
    );
  },

  // =========================================================
  // SEND EMAIL OTP
  // =========================================================
  sendOtp: ({ email, otpEmailSubject }) => {
    const normalizedEmail = encodeURIComponent(
      String(email || "")
        .trim()
        .toLowerCase(),
    );

    const normalizedSubject = encodeURIComponent(
      String(otpEmailSubject || "D2i2a w Noss OTP Verification").trim(),
    );

    return Utils.controller(
      "api/user/v1/authenticate/sendOtp",
      "",
      "GET",
      `email=${normalizedEmail}&otpEmailSubject=${normalizedSubject}`,
      false,
      "",
      true,
    );
  },

  // =========================================================
  // VERIFY EMAIL OTP
  // =========================================================
  verifyEmailOtp: ({ verifyOtpDTO }) => {
    return Utils.controller(
      "api/user/v1/authenticate/verifyEmailOTP",
      verifyOtpDTO,
      "POST",
      "",
      false,
      "",
      true,
    );
  },

  // =========================================================
  // CHECK PASSWORD
  // =========================================================
  checkPassword: ({ checkPassword }) => {
    return Utils.controller(
      "api/user/v1/authenticate/checkPassword",
      checkPassword,
      "POST",
      "",
      false,
      "",
      true,
    );
  },

  // =========================================================
  // SET PASSWORD
  // =========================================================
  setPassword: ({ setPasswordDTO }) => {
    return Utils.controller(
      "api/user/v1/authenticate/setPassword",
      setPasswordDTO,
      "POST",
      "",
      false,
      "",
      true,
    );
  },

  // =========================================================
  // ACTIVATE USER
  // =========================================================
  activateUser: ({ email, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    return Utils.controller(
      "api/user/v1/activate",
      { email },
      "PUT",
      "",
      true,
      token,
      true,
    );
  },

  // =========================================================
  // CHECK PHONE NUMBER
  // GET /api/user/v1/checkExistsPhoneNumber
  // =========================================================
  checkExistsPhoneNumber: ({ phoneNumber, suffix }) => {
    const normalizedPhoneNumber = encodeURIComponent(
      String(phoneNumber || "").trim(),
    );

    return Utils.controller(
      "api/user/v1/checkExistsPhoneNumber",
      "",
      "GET",
      `phoneNumber=${normalizedPhoneNumber}&suffix=${suffix}`,
      false,
      "",
      true,
    );
  },

  // =========================================================
  // CHECK EMAIL
  // =========================================================
  checkIfEmailInUse: ({ email }) => {
    const normalizedEmail = encodeURIComponent(
      String(email || "")
        .trim()
        .toLowerCase(),
    );

    return Utils.controller(
      "api/user/v1/checkIfEmailInUse",
      "",
      "GET",
      `Email=${normalizedEmail}`,
      false,
      "",
      true,
    );
  },

  // =========================================================
  // CHANGE EMAIL
  // PUT /api/user/v1/changeEmail
  // Query: newEmail + userID
  // =========================================================
  changeEmail: ({ newEmail, userID, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const normalizedEmail = encodeURIComponent(
      String(newEmail || "")
        .trim()
        .toLowerCase(),
    );

    const normalizedUserID = encodeURIComponent(String(userID || "").trim());

    const query = `newEmail=${normalizedEmail}` + `&userID=${normalizedUserID}`;

    return Utils.controller(
      "api/user/v1/changeEmail",
      "",
      "PUT",
      query,
      true,
      token,
      true,
    );
  },

  // =========================================================
  // SEND SMS OTP
  // =========================================================
  sendSMSOTP: ({ phoneNumber, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const normalizedPhoneNumber = encodeURIComponent(
      String(phoneNumber || "").trim(),
    );

    return Utils.controller(
      "api/user/v1/sendSMSOTP",
      "",
      "GET",
      `phoneNumber=${normalizedPhoneNumber}`,
      true,
      token,
      true,
    );
  },

  // =========================================================
  // VERIFY SMS OTP
  // =========================================================
  verifySMSOTP: ({ verifyOtpDTO, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    return Utils.controller(
      "api/user/v1/verifySMSOTP",
      verifyOtpDTO,
      "POST",
      "",
      true,
      token,
      true,
    );
  },

  // =========================================================
  // UPDATE PHONE NUMBER
  // PUT /api/user/v1/updateUserPhoneNumber
  // Query: newPhoneNumber + suffix + userId
  // =========================================================
  updateUserPhoneNumber: ({
    newPhoneNumber,
    suffix,
    userId,
    jwt,
    Authorization,
  }) => {
    const token = jwt || Authorization || "";

    const normalizedPhoneNumber = encodeURIComponent(
      String(newPhoneNumber || "").trim(),
    );

    const normalizedSuffix = encodeURIComponent(
      String(suffix || "")
        .replace(/\D/g, "")
        .trim(),
    );

    const normalizedUserId = encodeURIComponent(String(userId || "").trim());

    const query =
      `newPhoneNumber=${normalizedPhoneNumber}` +
      `&suffix=${normalizedSuffix}` +
      `&userId=${normalizedUserId}`;

    return Utils.controller(
      "api/user/v1/updateUserPhoneNumber",
      "",
      "PUT",
      query,
      true,
      Authorization,
      true,
    );
  },

  // =========================================================
  // SET / ADD / UPDATE USER ADDRESS
  // PUT /api/user/v1/address
  // =========================================================
  setUserAddress: ({ userAddress, AddressResponseDTO, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const request = userAddress || AddressResponseDTO || {};

    return Utils.controller(
      "api/user/v1/address",
      request,
      "PUT",
      "",
      true,
      token,
      true,
    );
  },

  // =========================================================
  // GET USER ADDRESSES
  // GET /api/user/v1/getAddress
  // Query: userID
  // =========================================================
  getUserAddress: ({ userID, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const normalizedUserID = encodeURIComponent(String(userID || "").trim());

    const query = `userID=${normalizedUserID}`;

    return Utils.controller(
      "api/user/v1/getAddress",
      "",
      "GET",
      query,
      true,
      token,
      true,
    );
  },

  // =========================================================
  // DELETE USER ADDRESS
  // DELETE /api/user/v1/user/deleteAddress
  // Query: userAddressID
  // =========================================================
  deleteUserAddress: ({ userAddressID, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const normalizedUserAddressID = encodeURIComponent(
      String(userAddressID || "").trim(),
    );

    const query = `userAddressID=${normalizedUserAddressID}`;

    return Utils.controller(
      "api/user/v1/user/deleteAddress",
      "",
      "DELETE",
      query,
      true,
      token,
      true,
    );
  },

  // =========================================================
  // DELETE USER ACCOUNT
  // DELETE /api/user/v1/deleteUserByUserId
  // Query: userID
  // =========================================================
  deleteUserAccount: ({ userID, jwt, Authorization }) => {
    const token = jwt || Authorization || "";

    const normalizedUserID = encodeURIComponent(String(userID || "").trim());

    const query = `userID=${normalizedUserID}`;

    return Utils.controller(
      "api/user/v1/deleteAccount",
      "",
      "DELETE",
      query,
      true,
      token,
      true,
    );
  },

};

export default userController;
